import type {
  ClientResource,
  ClientResourceContainer,
  ROLE,
} from '@/features/Clients';
import type { FeatureGroup, TariffFeature } from '@/features/Features';
import type { SearchData } from '@/features/Global/types';
import type { SortSelection } from '@/features/Tariffs/sorting';
import type {
  BaseTariff,
  TariffSearchRequestBody,
} from '@/features/Tariffs/types';
import type { Settings } from '@/features/Broker/settingsSchema';
import { EntityResourceContainer } from '@/features/EntityManager/types';
import { SECTION } from '@/components/layout/types';

export enum QUOTE_TYPE {
  DEFAULT = 'quote',
  INVENTORY_CONTRACT = 'inventoryContract',
}

export const QUOTE_RESET_AFTER_LOAD = [QUOTE_TYPE.INVENTORY_CONTRACT];

export interface LegacyClientMapData {
  roles: ROLE[];
  data: ClientResource;
}

// we extend SearchData partially because it could happen that some fields won't exist in the BE response
export interface LegacySearchData extends Partial<SearchData> {
  clientDaten?: {
    [key: string]: {
      geburtsdatum: string;
    };
  };
}

export interface LegacyQuoteContent {
  kunden: {
    [key in ROLE]?: {
      id?: number;
    } & Record<string, unknown>;
  };
  suche?: LegacySearchData;
  vergleich?: {
    empfehlung: BaseTariff | null;
    vergleichGroup: BaseTariff[];
  };
  filters?: {
    directAgreement: boolean;
  };
}

export type LegacyInventoryContractCallbackData =
  | Record<string, unknown>
  | Record<string, unknown>[];

export type LegacyInventoryContractCallback = (
  data: LegacyInventoryContractCallbackData,
) => LegacyInventoryContractCallbackData;

export type LegacyInventoryContractTransformer<T = Record<string, string>> = {
  [key in keyof T]?: LegacyInventoryContractCallback;
};

export interface VersicherungsangabenDirtyFields {
  [key: string]: {
    originalValue?: unknown; // could be undefined due to backend transformation
    isUndefinedOrNull?: boolean; // workaround to keed undefined / null values in the requests (undefined would be stripped by javascript requests, null by backend quote migrators)
  };
}

export interface QuoteContent<
  T extends EntityResourceContainer = EntityResourceContainer,
> extends Record<string, unknown> {
  version?: number;
  searchData: SearchData<T>;
  contractData?: Record<string, unknown>;
  companyQuestionData?: Record<string, unknown>;
  clients: ClientResourceContainer[];
  comparison: {
    recommendation: BaseTariff | null;
    selection: BaseTariff[];
  };
  filters: {
    directAgreement: boolean;
    search: string[];
    sorting: SortSelection[];
    blacklistedCompanies?: string[];
  };
  profileId?: number;
  versicherungsangaben?: {
    sections: SECTION[];
    helpInput: string;
    dirtyFields: VersicherungsangabenDirtyFields;
  };
}

export interface LegacyInventoryContractContent extends QuoteContent {
  antrag: Record<string, unknown>;
}

export const isLegacyInventoryContractContent = (
  obj: unknown,
): obj is LegacyInventoryContractContent =>
  'antrag' in (obj as LegacyInventoryContractContent);

/**
 * History:
 * V1-2 legacy quotes
 * V3 new entity structure
 */
export const QuoteVersion = 3;
export const LegacyQuoteVersion = 2;

export const isValidQuoteContent = (obj: unknown): obj is QuoteContent =>
  (obj as QuoteContent).version === QuoteVersion &&
  'searchData' in (obj as QuoteContent) &&
  'clients' in (obj as QuoteContent) &&
  'comparison' in (obj as QuoteContent) &&
  'filters' in (obj as QuoteContent);

export interface QuoteWithContractInputData extends QuoteContent {
  contractData: Record<string, unknown>;
}

export interface QuoteWithCompanyQuestionInputData extends QuoteContent {
  companyQuestionData: Record<string, unknown>;
}

export interface InventoryContract {
  clientId: number;
  content: QuoteWithContractInputData;
  title: string;
  tariffId: string;
}

export const hasCompanyQuestionInputData = (
  obj: unknown,
): obj is QuoteWithCompanyQuestionInputData =>
  'companyQuestionData' in (obj as QuoteWithCompanyQuestionInputData);

export const hasContractInputData = (
  obj: unknown,
): obj is QuoteWithContractInputData =>
  'contractData' in (obj as QuoteWithContractInputData);

export interface InventoryContractResponse {
  id: number;
  clientId: number;
}

export interface QuoteMeta {
  id: number;
  title: string;
  clientId: number;
}

export interface Quote<
  T extends EntityResourceContainer = EntityResourceContainer,
> extends QuoteMeta {
  content: QuoteContent<T>;
}

export interface GetAllQuoteEntity extends Record<string, unknown> {
  id: number;
  title: string;
  type: 'bestand' | 'angebot';
}

export interface WebreportBaseTariff extends BaseTariff {
  merkmale?: TariffFeature[];
}

export interface WebreportApiResponse<T = BaseTariff> {
  id: string;
  clientId: string;
  offerId: number;
  data: {
    tarife: WebreportBaseTariff[];
    userKonfiguration?: {
      userSettings?: Settings;
    };
    vorgaben: TariffSearchRequestBody;
    recommendation?: string;
    merkmale?: FeatureGroup[];
    allTimeTariffs?: T[] | T;
  };
}
